import { useCallback, useMemo, useState } from 'react';
import { CheckGlyph } from './ui/Glyphs';

/**
 * Onglet « MCP » de la page Réglages.
 *
 * Ne gère AUCUN secret : le connecteur MCP s'authentifie avec une clé API
 * ordinaire, créée dans l'onglet « API ». Dupliquer ici un bouton « nouvelle
 * clé » laisserait croire qu'il existe un second type de justificatif.
 *
 * Cet écran a donc un seul rôle : rendre la configuration trouvable depuis le
 * compte — la documentation publique (/docs/developer/mcp) et le bloc JSON à
 * coller dans Claude Desktop, pré-rempli pour ce déploiement.
 */

const DOC_URL = '/docs/developer/mcp';

function CopyButton({ value, label = 'Copier' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore — le bloc reste sélectionnable à la main
    }
  }, [value]);

  return (
    <button
      onClick={onCopy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors"
    >
      {copied && <CheckGlyph className="h-3.5 w-3.5" />}
      {copied ? 'Copié' : label}
    </button>
  );
}

export default function McpSection() {
  // Nom canonique de la surface publique : `api.<domaine>`, pas l'hôte de
  // l'application. Les deux pointent sur le même serveur (Express ne route pas
  // par hôte), mais `api.` est ce que le connecteur prend par défaut quand
  // `VISALY_API_BASE_URL` est absente — afficher autre chose ferait diverger
  // l'exemple du comportement réel. Hors ce cas (dev, self-host), on retombe
  // sur l'origine réellement servie.
  const apiBaseUrl = useMemo(() => {
    const raw =
      (import.meta.env.VITE_API_URL as string | undefined)?.trim() || window.location.origin;
    try {
      const url = new URL(raw);
      if (url.hostname.startsWith('app.')) {
        url.hostname = `api.${url.hostname.slice('app.'.length)}`;
      }
      return `${url.origin}/v1`;
    } catch {
      return `${raw.replace(/\/+$/, '')}/v1`;
    }
  }, []);

  const configJson = useMemo(
    () =>
      JSON.stringify(
        {
          mcpServers: {
            visaly: {
              command: 'npx',
              args: ['tsx', 'server/mcp.ts'],
              cwd: '/chemin/absolu/vers/Visaly',
              env: {
                VISALY_API_KEY: 'vsly_live_…',
                VISALY_API_BASE_URL: apiBaseUrl,
              },
            },
          },
        },
        null,
        2,
      ),
    [apiBaseUrl],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Connecteur MCP</h2>
          <p className="mt-1 text-sm text-slate-600">
            Lancez vos pré-scorings de conformité depuis Claude Desktop, Cursor ou tout
            client compatible Model Context Protocol. L'assistant appelle Visaly ; le
            pipeline, l'autorisation et les quotas restent côté serveur.
          </p>
        </div>
        <div className="shrink-0 flex flex-col gap-2">
          <a
            href={DOC_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-violet-300 text-violet-700 hover:bg-violet-50 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
            Ouvrir la documentation
          </a>
          <a
            href={DOC_URL}
            download="visaly-mcp.html"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" />
            </svg>
            Télécharger
          </a>
        </div>
      </div>

      {/* Facturation : une analyse MCP est une analyse Visaly, pas un essai gratuit. */}
      <div className="rounded-lg border border-violet-200 bg-violet-50 p-4 text-sm text-violet-900">
        <div className="font-medium">Les analyses MCP sont de vraies analyses</div>
        <p className="mt-1">
          Elles décomptent votre volume exactement comme celles lancées depuis
          l'application, et les analyses notées apparaissent dans votre historique.
          Le connecteur tourne sur votre poste et ne détient qu'un seul secret :
          votre clé API.
        </p>
      </div>

      {/* Étapes */}
      <div className="rounded-xl border border-slate-200 bg-cream-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900">Mise en place</h3>
        </div>
        <ol className="divide-y divide-slate-200 text-sm text-slate-700">
          <li className="px-5 py-4">
            <span className="font-medium text-slate-900">1. Créez une clé API</span>
            <p className="mt-1 text-slate-600">
              Onglet <strong>API</strong> → « Nouvelle clé ». Sa valeur n'est affichée
              qu'une seule fois. Le connecteur refuse tout ce qui n'est pas une clé{' '}
              <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">vsly_live_…</code>{' '}
              : un jeton de session ne convient pas.
            </p>
          </li>
          <li className="px-5 py-4">
            <span className="font-medium text-slate-900">2. Installez le connecteur</span>
            <p className="mt-1 text-slate-600">
              Node.js ≥ 20 et une copie du dépôt Visaly avec ses dépendances
              (<code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">npm install</code>).
              Le paquet{' '}
              <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">visaly-mcp</code>{' '}
              n'est pas encore publié sur npm.
            </p>
          </li>
          <li className="px-5 py-4">
            <span className="font-medium text-slate-900">3. Déclarez le serveur</span>
            <p className="mt-1 text-slate-600">
              Claude Desktop → Réglages → Développeur → Modifier la config, puis collez
              le bloc ci-dessous en remplaçant le chemin et la clé. Redémarrez
              complètement l'application.
            </p>
          </li>
          <li className="px-5 py-4">
            <span className="font-medium text-slate-900">4. Vérifiez</span>
            <p className="mt-1 text-slate-600">
              Depuis la racine du dépôt, avec la clé dans l'environnement :{' '}
              <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">npx tsx server/mcp.ts --check</code>.
              Le diagnostic ne lance aucune analyse — ni crédit, ni quota consommé.
            </p>
          </li>
        </ol>
      </div>

      {/* Bloc de configuration */}
      <div className="rounded-xl border border-slate-200 bg-cream-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900">
            Configuration Claude Desktop
          </h3>
          <CopyButton value={configJson} label="Copier la config" />
        </div>
        <pre className="px-5 py-4 overflow-x-auto text-xs font-mono text-slate-800 bg-slate-50">
          <code>{configJson}</code>
        </pre>
        <div className="px-5 py-4 border-t border-slate-200 text-xs text-slate-600 space-y-2">
          <p>
            <strong className="text-slate-800">cwd</strong> — chemin absolu de la racine
            du dépôt Visaly sur votre poste.
          </p>
          <p>
            <strong className="text-slate-800">VISALY_API_BASE_URL</strong> — facultative
            si sa valeur est <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{apiBaseUrl}</code>,
            que le connecteur prend déjà par défaut. À conserver pour pointer un autre
            déploiement.
          </p>
          <p>
            <strong className="text-slate-800">Windows</strong> — si le serveur ne démarre
            pas, remplacez{' '}
            <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">"command": "npx"</code>{' '}
            par{' '}
            <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">"command": "cmd"</code>{' '}
            et{' '}
            <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">"args": ["/c", "npx", "tsx", "server/mcp.ts"]</code>.
          </p>
        </div>
      </div>
    </div>
  );
}
